import type { StatisticSectionRow } from '../StatisticsSectionsTable/types';
import type { StatisticCell, StatisticCells } from '../statisticCells';
import {
  buildNestedRows,
  getActiveSectionMode,
  getSectionKey,
  getSectionRows,
} from '../StatisticsSectionsTable/sectionRows';

export interface StatisticsExportOptions {
  readonly direction: 'outbound' | 'inbound';
  readonly period: { readonly from: string; readonly to: string };
  readonly sections: readonly StatisticSectionRow[];
  readonly modes: Readonly<Record<string, string>>;
}

export interface StatisticsExportRow {
  readonly label: string;
  readonly depth: number;
  readonly emphasized: boolean;
  readonly cells: StatisticCells;
}

export interface StatisticsExportSection {
  readonly title: string;
  readonly modeLabel?: string;
  readonly rows: readonly StatisticsExportRow[];
  readonly modes?: readonly StatisticsExportMode[];
}

export interface StatisticsExportMode {
  readonly value: string;
  readonly label: string;
  readonly rows: readonly StatisticsExportRow[];
}

export interface StatisticsExportSnapshot {
  readonly sheetName: string;
  readonly fileName: string;
  readonly sections: readonly StatisticsExportSection[];
}

class StatisticsExportPeriodError extends Error {
  constructor() {
    super('Не удалось определить период статистики.');
    this.name = 'StatisticsExportPeriodError';
  }
}

export class StatisticsExportModeError extends Error {
  constructor(
    readonly sectionTitle: string,
    readonly reason: 'duplicate-label' | 'incompatible-layout',
  ) {
    super(`Не удалось добавить переключатель режимов блока «${sectionTitle}».`);
    this.name = 'StatisticsExportModeError';
  }
}

const formatDateOnly = (value: string): string => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) throw new StatisticsExportPeriodError();
  return `${parts[3]}.${parts[2]}.${parts[1]}`;
};

const copyCell = (cell: StatisticCell): StatisticCell => ({
  ...cell,
  value:
    cell.value instanceof Date ? new Date(cell.value.getTime()) : cell.value,
});

const labelWithSubtitle = (row: StatisticSectionRow) =>
  [row.parameter, row.subtitle].filter(Boolean).join('\n');

const snapshotRows = (
  rows: readonly StatisticSectionRow[],
  depth = 0,
): StatisticsExportRow[] =>
  rows.flatMap((row) => {
    const cells = row.cells ?? {
      value: { value: row.value ?? '—' },
      percent: { value: row.percent ?? '—' },
    };
    const rowDepth = Math.max(depth, row.level ?? 0);
    return [
      {
        label: labelWithSubtitle(row),
        depth: rowDepth,
        emphasized: Boolean(row.children?.length || row.isEmphasized),
        cells: {
          value: copyCell(cells.value),
          percent: copyCell(cells.percent),
        },
      },
      ...snapshotRows(row.children ?? [], rowDepth + 1),
    ];
  });

const snapshotModes = (
  section: StatisticSectionRow,
  options: StatisticsExportOptions,
  selectedRows: readonly StatisticsExportRow[],
): readonly StatisticsExportMode[] | undefined => {
  const modes = (section.modeOptions ?? []).flatMap((mode) =>
    section.childrenByMode?.[mode.value]
      ? [
          {
            value: mode.value,
            label: mode.label,
            rows: snapshotRows(
              buildNestedRows(
                getSectionRows(section, {
                  ...options.modes,
                  [getSectionKey(section)]: mode.value,
                }),
              ),
            ),
          },
        ]
      : [],
  );
  const selected = getActiveSectionMode(section, options.modes);
  if (modes.length < 2 || !modes.some((mode) => mode.value === selected)) {
    return undefined;
  }
  if (
    new Set(modes.map((mode) => mode.label.toLowerCase())).size !== modes.length
  ) {
    throw new StatisticsExportModeError(section.parameter, 'duplicate-label');
  }
  if (
    modes.some(
      (mode) =>
        mode.rows.length !== selectedRows.length ||
        mode.rows.some(
          (row, index) =>
            row.depth !== selectedRows[index]?.depth ||
            row.emphasized !== selectedRows[index]?.emphasized,
        ),
    )
  ) {
    throw new StatisticsExportModeError(
      section.parameter,
      'incompatible-layout',
    );
  }
  return modes;
};

export const createStatisticsExportSnapshot = (
  options: StatisticsExportOptions,
): StatisticsExportSnapshot => ({
  sheetName:
    options.direction === 'outbound'
      ? 'Статистика исходящих'
      : 'Статистика входящих',
  fileName: `statistics_${formatDateOnly(options.period.from)}-${formatDateOnly(options.period.to)}.xlsx`,
  sections: options.sections.map((section) => {
    const activeMode = getActiveSectionMode(section, options.modes);
    const modeLabel = section.modeOptions?.find(
      (mode) => mode.value === activeMode,
    )?.label;
    const rows = snapshotRows(
      buildNestedRows(getSectionRows(section, options.modes)),
    );
    const modes = snapshotModes(section, options, rows);
    return {
      title: labelWithSubtitle(section),
      modeLabel,
      rows,
      ...(modes ? { modes } : {}),
    };
  }),
});
